import React, { useCallback, useRef } from 'react'
import { useDrag } from 'react-dnd'
import Icon from 'components/Icon'

export const DRAG_TYPE = 'DASHBOARD_WIDGET'

const CONTROL =
  'flex h-6 w-6 items-center justify-center rounded border text-xs leading-none transition ' +
  'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:border-gray-400 disabled:text-gray-400'

const IDLE = 'border-gray-400 bg-white text-gray-700 hover:border-primary hover:text-primary'

/**
 * One widget's place in a column.
 *
 * Reading the dashboard and rearranging it are different jobs, so this renders
 * two different things. Normally it is a plain wrapper around the live widget.
 * While customizing it collapses to a compact card - a map of the layout rather
 * than a dimmed copy of it - because the live widgets run to tens of thousands
 * of pixels and nothing can be dragged across that.
 *
 * The children stay mounted and are hidden with CSS rather than unmounted, so
 * toggling customize mode never re-fires a widget's data fetch.
 */
const WidgetFrame = ({
  widget,
  label,
  columnIndex,
  index,
  customizing = false,
  canRemove = true,
  canMoveUp = true,
  canMoveDown = true,
  canMoveLeft = true,
  canMoveRight = true,
  onArrow,
  onRemove,
  onDragEnd,
  children,
}) => {
  const title = label || widget.widgetId.replace(/_/g, ' ')

  // react-dnd is on ^11.1.3: the drag type lives inside `item`. The v14
  // top-level `type` key is a silent no-op.
  //
  // columnIndex/index are captured at dragstart and stay valid for the whole
  // drag because nothing reorders until the drop - the column only draws an
  // insertion line while you hover.
  const [{ isDragging }, drag] = useDrag({
    item: { type: DRAG_TYPE, widgetId: widget.widgetId, columnIndex, index },
    canDrag: () => customizing,
    end: () => onDragEnd && onDragEnd(),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  // Only the handle starts a drag. connectDragSource() sets draggable="true"
  // unconditionally and a draggable element cannot have its text selected -
  // coordinators copy order names out of these widgets - so it is attached to
  // the handle button, and only while customizing.
  const attachHandle = useCallback((node) => {
    if (customizing) drag(node)
  }, [drag, customizing])

  const arrow = (direction, icon, enabled, description) => (
    <button
      type="button"
      aria-label={`Move ${title} ${description}`}
      title={`Move ${description}`}
      disabled={!enabled}
      onClick={() => onArrow(columnIndex, index, direction)}
      className={`${CONTROL} ${IDLE}`}
    >
      <Icon icon={icon} className="" />
    </button>
  )

  return (
    // While dragging the card is taken out of the flow entirely, so the column's
    // remaining cards are exactly the list the drop index is computed against.
    <li data-card className={isDragging ? 'hidden' : ''}>
      {customizing ? (
        <div className="rounded border border-gray-400 bg-white px-2 py-2 shadow-sm">
          <div className="flex items-center gap-2">
            <button
              ref={attachHandle}
              type="button"
              aria-label={`Drag ${title} to another position`}
              title="Drag to another position"
              className={`${CONTROL} cursor-move border-transparent text-gray-400 hover:text-primary`}
            >
              <Icon icon="bars" className="" />
            </button>

            <span className="grow truncate text-sm font-medium" title={title}>
              {title}
            </span>

            <button
              type="button"
              aria-label={`Remove ${title}`}
              title={canRemove ? 'Remove widget' : 'A dashboard needs at least one widget'}
              disabled={!canRemove}
              onClick={() => onRemove(columnIndex, index)}
              className={`${CONTROL} shrink-0 border-transparent text-gray-500 hover:text-negative disabled:border-transparent`}
            >
              <Icon icon="times" className="" />
            </button>
          </div>

          {/* Single-pointer alternative to dragging - WCAG 2.2 SC 2.5.7 (AA).
              A drag handle on its own does not satisfy that criterion.
              Up/down move within this column, left/right between columns. */}
          <div className="mt-2 flex items-center justify-end gap-1 border-t pt-2">
            {arrow('left', 'chevron-left', canMoveLeft, 'to the previous column')}
            {arrow('up', 'chevron-up', canMoveUp, 'up in this column')}
            {arrow('down', 'chevron-down', canMoveDown, 'down in this column')}
            {arrow('right', 'chevron-right', canMoveRight, 'to the next column')}
          </div>
        </div>
      ) : null}

      <div className={customizing ? 'hidden' : ''}>{children}</div>
    </li>
  )
}

export default WidgetFrame
